
import { map } from 'rxjs/operators';
import { Inject, Injectable } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';
import { EntityType } from '../../../../api/targeting/enum/entity.type.enum';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, Observable } from 'rxjs';
import { MentionsApi } from '../../../../api/mentions/mentions.api';
import { MentionsSearchResponseModel } from '../../../../api/mentions/model/mentions-search-response.model';
import { InfluencerStreamsApi } from '../../../../api/targeting/influencer-streams.api';
import { EntityIdApiModel } from '../../../../api/targeting/model/entity-id-api-model';
import { InfluencerStreamApiModel } from '../../../../api/targeting/model/influencer-stream.api.model';
import { TargetingMiscApi } from '../../../../api/targeting/targeting-misc.api';
import { JournodexRegionsService } from '../../../../Common/Journodex/services/journodex-regions.service';
import { OutletContactTabType, TargetingRhpContextsService } from '../../../../Common/TargetingShared/services/targeting-rhp-contexts.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { MentionsService } from '../../../../Monitor/Core/services/mentions.service';
import { Mention } from '../../../../Common/Mentions/models/mention';
import { TargetingSearch } from '../../models/targeting-search.model';
import { TargetingSearchService } from '../../services/targeting-search.service';
import { SearchModeEnum } from '../../../../Common/TargetingShared/enum/search-mode.enum';
import { IdNamePairModel } from '../../../../Common/Misc/models/id-name-pair.model';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';


// Extending MentionsService to provide the mention to the details tab
// I would prefer if the details tab accepted an input but this was pretty easy to do...
@Injectable()
export class InfluencerStreamsService extends MentionsService {

    // state in the UI
    streams: InfluencerStreamApiModel[];
    streams$: BehaviorSubject<InfluencerStreamApiModel[]> = new BehaviorSubject<InfluencerStreamApiModel[]>(null);
    // state in the database
    savedStreams: InfluencerStreamApiModel[];
    // for stream contacts RHP
    lastStreamContactUuids: string[];
    focusedStreamId$: BehaviorSubject<number> = new BehaviorSubject<number>(null);

    constructor(
        protected messageService: MessageService,
        protected mentionsApi: MentionsApi,
        protected localizationService: LocalizationService,
        protected journodexRegionsService: JournodexRegionsService,
        protected influencerStreamsApi: InfluencerStreamsApi,
        protected targetingMiscApi: TargetingMiscApi,
        public targetingRhpContextsService: TargetingRhpContextsService,
        public targetingSearchService: TargetingSearchService,
        protected currentUserStateService: CurrentUserStateService,
        @Inject (APP_BASE_HREF) public appBaseHref: string

    ) {
        super(messageService, mentionsApi, localizationService, journodexRegionsService, currentUserStateService, appBaseHref);
        this.influencerStreamsApi.getStreams().subscribe(streams => {
            this.streams = streams;
            this.savedStreams = JSON.parse(JSON.stringify(streams));
            this.streams$.next(streams);
        });
    }

    getStreams(): Observable<InfluencerStreamApiModel[]> {
        return this.streams$;
    }

    saveStream(index: number): Promise<void> {
        return new Promise<void>((resolve) => {
            // TODO Real equals might be better
            if (JSON.stringify(this.streams[index]) !== JSON.stringify(this.savedStreams[index])) {
                this.influencerStreamsApi.upsertStream(this.streams[index]).subscribe(id => {
                    this.streams[index].id = id;
                    this.savedStreams[index] = JSON.parse(JSON.stringify(this.streams[index]));
                    this.setStreamOrder();
                    resolve();
                });
            } else {
                resolve();
            }
        });
    }

    deleteStream(index: number, hideOutletContactTabs: boolean = false) {
        if (this.streams[index].id) {
            if (hideOutletContactTabs)  {
                this.targetingRhpContextsService.outletContactTabsVisible$.next(false);
            }
            this.influencerStreamsApi.deleteStream(this.streams[index].id).subscribe(done => {
                this.streams.splice(index, 1, {});
                this.savedStreams.splice(index, 1, {});
                this.streams$.next(this.streams);
            });
        } else {
            // User created stream but clicked apply without selecting a source,
            // just treat it as an undo and give him back the new stream button
            this.streams.splice(index, 1);
            this.savedStreams.splice(index, 1);
            this.streams$.next(this.streams);
        }
    }

    setStreamOrder() {
        this.influencerStreamsApi.setStreamOrder(this.streams.map(x => x.id)).subscribe();
    }

    getStreamContent(index: number, lastFetch: Date, start: number): Observable<MentionsSearchResponseModel> {
        return this.influencerStreamsApi.getStreamContent(this.streams[index].id, lastFetch, start).pipe(
            map(x => {
                // It's undetermined which stream's top mention will be selected; how exciting
                if (!this.focusedMention && x.mentions.length > 0) {
                    const contactUuids = x.mentions.map(mention => {
                        return mention.contactUuid;
                    });
                    this.setFocusedMention(new Mention(x.mentions[0]), false, contactUuids, this.streams[index].id);
                }
                return x;
            }));
    }

    applyTwitterInfo(mentions: Mention[]) {

        this.targetingMiscApi.getTwitterInfo(mentions.map(mention => {
            return <EntityIdApiModel> {
                contactUuid: mention.author.uuid
            };
        })).subscribe(twitterInfos => {
            twitterInfos.forEach(info => {
                mentions.filter(x => x.author.uuid === info.id.contactUuid).forEach((contact) => {
                    contact.twitterHandle = info.twitterHandle;
                    contact.avatarUrl = info.avatarUrl;
                });
            });
        });

    }

    /**
     * Handles when a stream mention is selected
     * @param mention selected mention
     * @param openOutletTab open outlet tab in RHP
     * @param contactUuids all the distinct contact ids present in visible mentions
     * @param streamId focused stream id
     */
    setFocusedMention(mention: Mention, openOutletTab: boolean = false, streamContactUuids?: string[], streamId?: number) {
        this.focusedMention = mention;
        this.focusedMentionProvider.next(this.focusedMention);
        this.focusedStreamId$.next(streamId);

        // check for visible stream entity ids in order to search all contacts of the focused stream,
        // compare these ids with the last ids to prevent from doing the same search again
        if (streamContactUuids && JSON.stringify(this.lastStreamContactUuids) !== JSON.stringify(streamContactUuids)) {
            this.lastStreamContactUuids = streamContactUuids;
            this.searchStreamContacts(this.lastStreamContactUuids);
        } else if (!streamContactUuids) {
            this.targetingSearchService.clearSearch();
        }

        if (!openOutletTab && !mention?.author.uuid && mention?.publication.uuid) {
            openOutletTab = true;
        }

        if (openOutletTab) {
            this.targetingRhpContextsService.setOutlet(mention?.publication.uuid);
        } else {
            this.targetingRhpContextsService.setContact(mention?.author.uuid, null, OutletContactTabType.contactProfile, mention?.publication.uuid);
            this.targetingRhpContextsService.fetchPrivateData(mention?.author.uuid, EntityType.CONTACT);
        };
    }

    /**
     * Collects all the uuids of contacts currently shown in stream and pass those to targeting search query
     * @param streamMentions all visible mentions in stream
     */
    searchStreamContacts(contactUuids: string[]) {
        const search = new TargetingSearch();
        search.searchMode = SearchModeEnum.contact;
        contactUuids.forEach(uuid => search.contactIdsNames.include.push(new IdNamePairModel(undefined, uuid)));
        if (search.contactIdsNames.include.length > 0) {
            this.targetingSearchService.performSearch(search);
        }
    }

    /**
     * Returns whether the given streamID is the focused stream
     * @param streamId focused stream id
     */
    isFocusedStream(streamId: number): boolean {
        return streamId === this.focusedStreamId$.value;
    }
}
